/*** IMPORTS FROM imports-loader ***/
var define = false;

// src/js/kira.js

import { gsap } from "gsap";

class Kira {
    constructor(scrollmagic, params = {}) {
        this.scrollmagic = scrollmagic;
        this.defaults = this.initDefaults();
        this.params = this.initParams(params);
        this.timelines = {};
        this.scenes = {};
        this.tweens = {};
        this.scrollMagicController = null;
    }

    initDefaults() {
        return {
            debug: false,
            loadEvent: {
                domElement: null,
                eventName:  null
            },
            optimize: false,
            options: {
                start: "-=0.4",
                triggerHook: "onCenter",
                reverse: true
            },
            selectors: {
                onLoadWrappers: {
                    element: $("[data-kira-timeline-onload]"),
                    data:    "kira-timeline-onload"
                },
                onScrollWrappers: {
                    element: $("[data-kira-timeline]"),
                    data:    "kira-timeline"
                },
                item: {
                    element: $("[data-kira-item]"),
                    data:    "kira-item"
                }
            }
        };
    }

    initParams(params) {
        let paramsObj = { ...this.defaults };
        const { debug, loadEvent, optimize, options } = params;

        if (this.isDefined(debug))      paramsObj.debug       = debug;
        if (this.isDefined(loadEvent))  paramsObj.loadEvent   = { ...paramsObj.loadEvent, ...loadEvent };
        if (this.isDefined(optimize))   paramsObj.optimize    = optimize;
        if (this.isDefined(options))    paramsObj.options     = { ...paramsObj.options, ...options };

        return paramsObj;
    }

    init() {
        if (this.scrollMagicIsDefined()) {
            this.runOnloadAnimations();
            this.runOnScrollAnimations();
        }
    }

    setTimeline(timelineName, paused, $selector) {
        const _this = this;
        const timeline = gsap.timeline({
            paused,
            onStart() {
                _this.dispachEvent($selector, "kira:timelineStart", { timeline: this });
            },
            onUpdate() {
                _this.dispachEvent($selector, "kira:timelineUpdate", { timeline: this });
            },
            onComplete() {
                _this.dispachEvent($selector, "kira:timelineComplete", { timeline: this });
            }
        });

        _this.registerTimeline(timelineName, timeline);
        return timeline;
    }

    registerTimeline(timelineName, timeline) {
        if (this.isDefined(timelineName)) {
            this.timelines[String(timelineName)] = timeline;
        }
    }

    runOnloadAnimations() {
        const { domElement, eventName } = this.params.loadEvent;
        if (domElement && eventName) {
            domElement.on(eventName, () => this.initTimelines("onLoadWrappers"));
        } else {
            this.initTimelines("onLoadWrappers");
        }
    }

    initTimelines(selectorKey, paused = true, autoplay = true) {
        const selector     = this.params.selectors[selectorKey];
        const $elements    = selector.element;
        const dataAttr     = selector.data;

        $elements.each((_, el) => {
            const $el = $(el);
            if (this.isValidTimeline($el)) {
                const name     = $el.data(dataAttr);
                const timeline = this.setTimeline(name, paused, $el);
                this.generateTimelineTweens({ selector: $el, timeline, timelineName: name });
                autoplay && timeline.play();
            }
        });
    }

    runOnScrollAnimations() {
        const controller = this.getScrollMagicController(this.scrollmagic);
        const sel       = this.params.selectors.onScrollWrappers;
        const scenes    = [];

        sel.element.each((_, el) => {
            scenes.push(this.createScene($(el), sel.data));
        });
        controller.addScene(scenes);
    }

    getScrollMagicController(ScrollMagic) {
        if (!this.scrollMagicController) {
            this.scrollMagicController = new ScrollMagic.Controller();
        }
        return this.scrollMagicController;
    }

    createScene($selector, dataName) {
        let scene = null;
        if (this.isValidTimeline($selector)) {
            const timelineName = $selector.data(dataName);
            const timeline     = this.setTimeline(timelineName, false, $selector);
            const options      = this.getSceneOptions($selector);

            this.generateTimelineTweens({ selector: $selector, timeline, timelineName });
            scene = new this.scrollmagic.Scene(options).setTween(timeline);
            this.params.debug && scene.addIndicators();
            this.registerScene(timelineName, scene);
        }
        return scene;
    }

    registerScene(timelineName, scene) {
        this.scenes[timelineName] = scene;
    }

    getSceneOptions($selector) {
        const { triggerHook, reverse } = $selector.data();
        const def                      = this.params.options;
        return {
            triggerElement: $selector,
            triggerHook:    this.isDefined(triggerHook) ? triggerHook : def.triggerHook,
            reverse:        this.isDefined(reverse)     ? reverse     : def.reverse
        };
    }

    generateTimelineTweens({ selector: $selector, timeline }) {
        const itemSel  = this.params.selectors.item;
        $selector.find(itemSel.element).each((i, el) => {
            const $item     = $(el);
            const key       = $item.data(itemSel.data);
            const start     = $item.data("start") ?? (i === 0 ? "start" : this.params.options.start);
            this.tweens[key]?.($item, timeline, start);
        });
    }

    add(index, callback) {
        this.tweens[index] = callback;
    }

    isValidTimeline($selector) {
        const name = $selector.data("kira-timeline");
        let ok     = true, msg;
        if (name && this.timelines[name]) {
            ok  = false;
            msg = this.getMessage("isValidTimelineDuplicate", name);
        }
        return this.control(ok, msg, $selector);
    }

    scrollMagicIsDefined() {
        return this.control(this.isDefined(this.scrollmagic), this.getMessage("scrollMagicIsDefined"));
    }

    dispachEvent($el, eventName, data = null) {
        const e = $.Event(eventName);
        if (data) $.each(data, (k, v) => e[k] = v);
        $el.trigger(e);
    }

    isDefined(v) {
        return typeof v !== "undefined";
    }

    control(condition, message, selector = null) {
        if (!condition) {
            selector
                ? console.error(message, selector)
                : console.error(message);
        }
        return condition;
    }

    getMessage(key, var1 = "") {
        const msgs = {
            scrollMagicIsDefined:      "ScrollMagic is required to init Kira.",
            isValidTimelineDuplicate:  "You already have a timeline called " + var1 + ", rename it."
        };
        return "Kira: " + (msgs[key] || "");
    }
}

export default Kira;

